import { lazy } from 'react';

import { PLAYGROUND_LABS, type PlaygroundLabPath } from './lib/labs';

import type { ReactElement } from 'react';
import type { RouteObject } from 'react-router-dom';

/**
 * Rotas do playground visual.
 *
 * `lazy` em cada lab, e não só na área: as páginas não são fluxo de produto e
 * ninguém chega nelas navegando, então nenhuma delas deve pesar no bundle de
 * quem abre o app para usar. Isso continua valendo — o chunk de um lab só é
 * baixado quando alguém abre aquele lab.
 *
 * ## ⏳ O gate de `DEV` está levantado, temporariamente
 *
 * Até 2026-07-23 esta área era montada só com `import.meta.env.DEV`: o Vite
 * trocava a expressão por `false`, o Rollup descartava os `import()` daqui e
 * nenhum chunk do playground chegava ao bundle publicado — a área ficava
 * inalcançável em produção, inclusive para quem digitasse a URL.
 *
 * O gate saiu porque o time de **design** acompanha o app pelo endereço
 * publicado, não por `pnpm dev`, e o playground passou a ter porta no menu
 * (`config/navigation.ts`) para eles. O custo é consciente: um banco de testes
 * interno fica alcançável por qualquer visitante de um app de emergência.
 *
 * **Isso é andaime.** Quando o acompanhamento de design não precisar mais,
 * devolva o gate abaixo e tire o item do menu no mesmo commit:
 *
 * ```ts
 * export const playgroundRoutes = import.meta.env.DEV ? buildPlaygroundRoutes() : [];
 * ```
 */
function buildPlaygroundRoutes(): RouteObject[] {
  const PlaygroundIndexPage = lazy(() => import('./routes/PlaygroundIndex.page'));
  const BrandPage = lazy(() => import('./routes/Brand.page'));
  const DesignSystemPage = lazy(() => import('./routes/DesignSystem.page'));
  const IconsPage = lazy(() => import('./routes/Icons.page'));
  const MapLegendPage = lazy(() => import('./routes/MapLegend.page'));
  const AnimationsPage = lazy(() => import('./routes/Animations.page'));

  // Mapa exaustivo: lab novo em `lib/labs.ts` sem tela aqui não compila.
  const labElements: Record<PlaygroundLabPath, ReactElement> = {
    'design-system': <DesignSystemPage />,
    icons: <IconsPage />,
    brand: <BrandPage />,
    animacoes: <AnimationsPage />,
    'map-legend': <MapLegendPage />,
  };

  return [
    {
      path: 'playground',
      children: [
        { index: true, element: <PlaygroundIndexPage /> },
        ...PLAYGROUND_LABS.map((lab) => ({ path: lab.path, element: labElements[lab.path] })),
      ],
    },
  ];
}

export const playgroundRoutes: RouteObject[] = buildPlaygroundRoutes();
